import axios from "axios";
import { useState, useEffect } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ss_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  let dev = localStorage.getItem("ss_device");
  if (!dev) {
    dev = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("ss_device", dev);
  }
  config.headers["X-Device-Id"] = dev;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 403 && error?.response?.data?.detail === "EMAIL_NOT_VERIFIED") {
      window.dispatchEvent(new CustomEvent("ss:verify-required"));
    }
    // Distinguish a genuine expired/invalid session (real 401 on an authenticated call)
    // from a transient backend outage (network error / 5xx). Only a real 401 clears the
    // token; transient errors leave it intact so the user isn't logged out on a restart.
    const isAuthEntry = url.includes("/auth/login") || url.includes("/auth/register");
    // Permanently banned account: kill the session immediately on any authenticated call so the
    // banned user is logged out and bounced to the login screen (where the block message shows).
    if (status === 403 && ["ACCOUNT_BANNED", "ACCOUNT_ARCHIVED"].includes(error?.response?.data?.detail) && !isAuthEntry) {
      localStorage.removeItem("ss_token");
      window.dispatchEvent(new CustomEvent("ss:session-expired"));
    }
    if (status === 401 && !isAuthEntry && localStorage.getItem("ss_token")) {
      localStorage.removeItem("ss_token");
      window.dispatchEvent(new CustomEvent("ss:session-expired"));
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const maskIban = (iban) => {
  const s = String(iban || "").replace(/\s/g, "").toUpperCase();
  return s.length >= 8 ? `${s.slice(0, 4)} •••• •••• •••• ${s.slice(-4)}` : s;
};

export const AED = (n) => {
  const v = Number(n || 0);
  const hasFils = Math.round(v * 100) % 100 !== 0;
  return `AED ${v.toLocaleString("en-US", { minimumFractionDigits: hasFils ? 2 : 0, maximumFractionDigits: 2 })}`;
};

// Seller cancellation / auto-cancel penalty preview: higher of AED 10 or 3% of total, rounded.
export const cancelPenalty = (total) => Math.max(10, Math.round(0.03 * Number(total || 0)));

// Delivery fees come from the backend's central config (GET /config/delivery-fees) so the
// values only ever live in one place (server.py DELIVERY_FEES). Cached at module level.
let _deliveryFeesConfig = null;
let _deliveryFeesPromise = null;
export function getDeliveryFeesConfig() {
  if (_deliveryFeesConfig) return Promise.resolve(_deliveryFeesConfig);
  if (!_deliveryFeesPromise) {
    _deliveryFeesPromise = api.get("/config/delivery-fees")
      .then((r) => { _deliveryFeesConfig = r.data; return _deliveryFeesConfig; })
      .catch((e) => { _deliveryFeesPromise = null; throw e; });
  }
  return _deliveryFeesPromise;
}
export function useDeliveryFees() {
  const [config, setConfig] = useState(_deliveryFeesConfig);
  useEffect(() => {
    let active = true;
    getDeliveryFeesConfig().then((c) => { if (active) setConfig(c); }).catch(() => {});
    return () => { active = false; };
  }, []);
  return config;
}
// Pure lookup against a fetched config — no hardcoded fee values. Returns null until loaded.
export function deliveryFeeForBox(box, config) {
  if (!config || !config.fees) return null;
  const fees = config.fees;
  const target = String(box || config.default_box || "").trim().toLowerCase();
  const match = Object.keys(fees).find((k) => k.toLowerCase() === target)
    || Object.keys(fees).find((k) => k.toLowerCase() === String(config.default_box || "").toLowerCase());
  return match ? fees[match] : null;
}

const TZ = "Asia/Dubai";
const _toDate = (v) => {
  if (!v) return null;
  const d = new Date(String(v).length === 10 ? `${v}T00:00:00Z` : v);
  return isNaN(d) ? null : d;
};
// "1 July 2026"
export const fmtDate = (v) => {
  if (!v) return "";
  const d = _toDate(v);
  if (!d) return v;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: TZ });
};
// "1 July 2026, 4:52 pm GST" — date-only inputs render as "1 July 2026"
export const fmtDateTime = (v) => {
  if (!v) return "";
  const d = _toDate(v);
  if (!d) return v;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: TZ });
  if (String(v).length === 10) return date;
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ }).toLowerCase();
  return `${date}, ${time} GST`;
};
// "1 Jul 2026" — short form for tight admin columns and compact cards
export const fmtShort = (v) => {
  if (!v) return "";
  const d = _toDate(v);
  if (!d) return v;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: TZ }).replace("Sept", "Sep");
};
export const fmtAdmin = fmtShort;

export default api;

export async function downloadReceipt(tid, ref) {
  const { data } = await api.get(`/transactions/${tid}/receipt`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `SellStuff-Receipt-${ref || (tid || "").slice(0, 8).toUpperCase()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function startPurchase(listingId, negId, tcsAccepted, photoAck) {
  try {
    // Clicking Buy Now / Proceed to checkout is a commit → reserve the item before sending the buyer
    // to Stripe so it can't be sold to someone else mid-payment. Idempotent for an already-agreed hold.
    try {
      await api.post(`/listings/${listingId}/lock`);
    } catch (e) {
      if (e?.response?.status === 423) {
        const { toast } = await import("sonner");
        toast.error(e?.response?.data?.detail || "This item is currently being purchased.");
        return;
      }
      if (e?.response?.status === 401) { window.location.href = "/login"; return; }
      // other lock errors: fall through — the purchase endpoint re-validates availability
    }
    const { data } = await api.post("/payments/purchase", {
      origin_url: window.location.origin,
      listing_id: listingId,
      negotiation_id: negId || null,
      buyer_tcs_accepted: !!tcsAccepted,
      buyer_tcs_accepted_at: tcsAccepted ? new Date().toISOString() : null,
      buyer_photo_ack: !!(photoAck && photoAck.ack),
      buyer_photo_ack_at: photoAck && photoAck.ack ? (photoAck.ack_at || new Date().toISOString()) : null,
      buyer_photos_viewed: (photoAck && photoAck.viewed) || [],
    });
    window.location.href = data.checkout_url;
  } catch (e) {
    if (e?.response?.status === 401) { window.location.href = "/login"; return; }
    const { toast } = await import("sonner");
    toast.error(e?.response?.data?.detail || "Could not start checkout");
  }
}


export async function settleBalance() {
  try {
    const { data } = await api.post("/payments/settle-balance", { origin_url: window.location.origin });
    if (data.checkout_url) window.location.href = data.checkout_url;
  } catch (e) {
    const { toast } = await import("sonner");
    toast.error(e?.response?.data?.detail || "Could not start payment");
  }
}
